import { API_BASE_URL } from '../../../shared/constants/env';

export type MessagingSurface = 'tab' | 'drawer' | 'widget';

export type MessagingLaunchPayload = {
  orderId?: string;
  threadId?: number;
  threadTitle?: string;
  targetContactId?: number;
  targetContactName?: string;
  targetContactEmail?: string;
  draftText?: string;
  preferredSurface?: MessagingSurface;
};

export type MessagingConversation = {
  threadId: number;
  threadTitle: string;
  targetContactId?: number;
  targetContactName?: string;
  targetContactEmail?: string;
  draftText?: string;
};

type ContactOption = {
  id: number;
  name: string;
  role?: string;
  team?: string;
  email?: string;
};

type ResolveConversationArgs = MessagingLaunchPayload & {
  senderFallbackContactId: number;
  senderName: string;
  senderEmail?: string;
  preferredLanguage?: string;
};

const apiRoot = API_BASE_URL.replace(/\/graphql\/?$/, '');

let contactsCache: ContactOption[] | null = null;

const normalizeValue = (value?: string) => String(value || '').trim().toLowerCase();

const fetchContacts = async (): Promise<ContactOption[]> => {
  if (contactsCache) {
    return contactsCache;
  }
  const res = await fetch(`${apiRoot}/api/v1/messaging/contacts`);
  if (!res.ok) {
    throw new Error('Failed to load messaging contacts');
  }
  const payload = await res.json();
  contactsCache = Array.isArray(payload?.contacts) ? payload.contacts : [];
  return contactsCache || [];
};

const findContact = (contacts: ContactOption[], email?: string, name?: string, id?: number) => {
  if (id) {
    const matchedById = contacts.find((contact) => Number(contact.id) === Number(id));
    if (matchedById) {
      return matchedById;
    }
  }

  const normalizedEmail = normalizeValue(email);
  if (normalizedEmail) {
    const matchedByEmail = contacts.find((contact) => normalizeValue(contact.email) === normalizedEmail);
    if (matchedByEmail) {
      return matchedByEmail;
    }
  }

  const normalizedName = normalizeValue(name);
  if (normalizedName) {
    return contacts.find((contact) => normalizeValue(contact.name) === normalizedName);
  }

  return undefined;
};

export const resolveMessagingConversation = async ({
  orderId,
  threadId,
  threadTitle,
  targetContactId,
  targetContactName,
  targetContactEmail,
  draftText,
  senderFallbackContactId,
  senderName,
  senderEmail,
  preferredLanguage = 'en',
}: ResolveConversationArgs): Promise<MessagingConversation> => {
  const contacts = await fetchContacts();
  const senderContact = findContact(contacts, senderEmail, senderName, senderFallbackContactId);
  const targetContact = findContact(contacts, targetContactEmail, targetContactName, targetContactId);

  if (senderContact && targetContact && senderContact.id !== targetContact.id) {
    const res = await fetch(`${apiRoot}/api/v1/messaging/direct-thread`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sender_contact_id: senderContact.id,
        sender_name: senderName,
        target_contact_id: targetContact.id,
        preferred_language: preferredLanguage,
      }),
    });

    if (res.ok) {
      const payload = await res.json();
      if (payload?.thread_id) {
        return {
          threadId: Number(payload.thread_id),
          threadTitle: payload.thread_title || threadTitle || `Direct: ${targetContact.name}`,
          targetContactId: targetContact.id,
          targetContactName: targetContact.name,
          targetContactEmail: targetContact.email,
          draftText,
        };
      }
    }
  }

  const fallbackThreadId = threadId || (orderId ? Number.parseInt(orderId.replace(/\D/g, ''), 10) || 1 : 1);
  return {
    threadId: fallbackThreadId,
    threadTitle: threadTitle || (orderId ? `Order ${orderId} Communication` : 'Messaging'),
    targetContactId: targetContact?.id,
    targetContactName: targetContact?.name || targetContactName,
    targetContactEmail: targetContact?.email || targetContactEmail,
    draftText,
  };
};
